import { styled } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import checked from '../../static/images/mvp/checked.png';
import searchIcon from '../../static/svg/ic-search-black.svg';
import FormControl from '@mui/material/FormControl';
import React, { useState } from 'react';
import { InputLabel } from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';
import { I18n } from '../../helpers/i18n';

export const formTheme = {
  formSection: {
    fontFamily: 'Questrial',
    fontWeight: 400,
    padding: '32px 36px',
    mimWidth: '438px'
  },
  sectionHeader: {
    color: '#000000',
    fontSize: '20px',
    lineHeight: '30px',
  },
  inputLabel: {
    color: '#000000',
    fontSize: '16px',
    lineHeight: '16px',
  },
  sectionFooter: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  button: {
    backgroundColor: '#FF9900',
    borderRadius: '16px',
    padding: '22px 44px',
    fontSize: '16px',
    lineHeight: '16px',
    textTransform: 'none',
    letterSpacing: 0,
  },
  formField: {
    marginBottom: '16px'
  }
}

const CustomSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    color: '#11827',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    borderColor: '#CCCCCC',
    borderRadius: '4px',
    fontFamily: 'Questrial',
    padding: '14px 16px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #CCCCCC',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #CCCCCC',
  },
}));

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  '&.MuiMenuItem-root': {
    padding: '12px 16px',
    color: '#6B7280',
    '&:hover': {
      backgroundColor: '#F9FAFB',
      padding: '12px 16px',
      borderRadius: '10px',
    },
    '&.Mui-selected': {
      backgroundColor: '#F9FAFB',
      color: '#FF9900',
      padding: '12px 16px',
      borderRadius: '10px',
      '&::after': {
        content: `url(${checked})`,
        position: 'absolute',
        right: 18,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      },
      position: 'relative'
    },
  },
}));

const autocompletePaperSx = {
  border: 'none',
  borderRadius: '10px',
  padding: '16px 24px',
  '& .MuiAutocomplete-listbox': {
    padding: '0px',
  },
};

const autocompleteTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Questrial',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    color: '#111827',
    padding: '0 !important',
    '& fieldset': {
      borderColor: '#CCCCCC',
      borderRadius: '4px',
    },
    '&:hover fieldset': {
      border: '1px solid #CCCCCC',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #CCCCCC',
    },
    '& .MuiAutocomplete-input': {
      padding: '14px 16px !important',
    },
  },
};

const autocompleteOptionSx = {
  padding: '12px 16px !important',
  color: '#6B7280',
  fontFamily: 'Questrial',
  fontSize: '16px',
  borderRadius: '10px',
  position: 'relative',
  '&:hover': {
    backgroundColor: '#F9FAFB !important',
  },
  '&[aria-selected="true"]': {
    backgroundColor: '#F9FAFB !important',
    color: '#FF9900',
    '&::after': {
      content: `url(${checked})`,
      position: 'absolute',
      right: 18,
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
    },
  },
};

export const CustomSelectComponent = ({
  options = [],
  selectedValue,
  onChange,
  title = null,
  required = true,
  placeholder = '',
  isSearchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const placeholderLabel = placeholder || '';

  if (isSearchable) {
    const selectedOption = options.find((option) => option.value === selectedValue) || null;

    return (
      <FormControl fullWidth>
        {title && (
          <InputLabel theme={formTheme}>{title}
            {required && (<span className="text-danger">*</span>)}
          </InputLabel>
        )}
        <Autocomplete
          options={options}
          value={selectedOption}
          getOptionLabel={(option) => option.label || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={(_, option) => {
            onChange({ target: { value: option ? option.value : '' } });
          }}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          noOptionsText={placeholderLabel}
          forcePopupIcon
          popupIcon={isOpen ? <img src={searchIcon} alt="" width={20} height={20} /> : undefined}
          sx={{
            '& .MuiAutocomplete-popupIndicatorOpen': {
              transform: 'none',
            },
          }}
          ListboxProps={{
            sx: {
              '& .MuiAutocomplete-option': autocompleteOptionSx,
            },
          }}
          componentsProps={{
            paper: {
              sx: autocompletePaperSx,
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholderLabel}
              sx={autocompleteTextFieldSx}
              inputProps={{
                ...params.inputProps,
                'aria-label': I18n.t('without_label'),
              }}
            />
          )}
        />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth>
      {title && (
        <InputLabel theme={formTheme}>{title}
          {required && (<span className="text-danger">*</span>)}
        </InputLabel>
      )}
      <CustomSelect
        value={selectedValue}
        onChange={onChange}
        MenuProps={{
          PaperProps: {
            sx: {
              border: 'none',
              borderRadius: '10px',
              padding: '16px 24px',
              '& .MuiList-root': {
                padding: '0px',
              },
            },
          },
        }}
        displayEmpty
        inputProps={{ 'aria-label': I18n.t('without_label') }}
      >
        {placeholder && (
          <MenuItem disabled value="">
            {placeholderLabel}
          </MenuItem>
        )}
        {options.map((option) => (
          <CustomMenuItem
            key={option.value} value={option.value}
            selected={option.value === selectedValue}
          >
            {option.label}
          </CustomMenuItem>
        ))}
      </CustomSelect>
    </FormControl>
  );
};
